const handleZeroOrEmpty = (num: number | undefined) => {
    if (num === 0 || !num) {
        return { value: 0, str: '0', suffix: '' };
    }
    return null;
};

const processNumberWithRounding = (num: number, roundToInteger?: boolean) => {
    if (!roundToInteger) {
        return { isZero: false, value: num };
    }

    const rounded = Math.round(num);
    if (rounded === 0) {
        return { isZero: true, value: 0 };
    }
    return { isZero: false, value: rounded };
};

const getDecimalPlaces = (
    decimalPlaces: number | undefined,
    processedNum: number,
    roundToInteger?: boolean
) => {
    if (roundToInteger) return 0;
    return decimalPlaces ?? (processedNum < 10 ? 2 : 1);
};

const formatSmallNumber = (computedDecimalPlaces: number) => {
    return computedDecimalPlaces === 0 ? '<1' : `<0.${'0'.repeat(computedDecimalPlaces - 1)}1`;
};

const scaleConfigs = [
    { threshold: 1000, divisor: 1000, suffix: 'K' },
    { threshold: 1000000, divisor: 1000000, suffix: 'M' },
    { threshold: 1000000000, divisor: 1000000000, suffix: 'B' },
    { threshold: 1000000000000, divisor: 1000000000000, suffix: 'T' },
];

const formatLargeNumber = (processedNum: number, factor: number) => {
    // Find the appropriate scale by checking which threshold the number exceeds
    for (let i = scaleConfigs.length - 1; i >= 0; i -= 1) {
        const config = scaleConfigs[i];
        if (processedNum >= config.divisor) {
            const value = Math.floor(processedNum / (config.divisor / factor)) / factor;
            return { value, str: String(value), suffix: config.suffix };
        }
    }

    // This should never happen for numbers >= 1000, but fallback to K
    const firstConfig = scaleConfigs[0];
    const value = Math.floor(processedNum / (firstConfig.divisor / factor)) / factor;
    return { value, str: String(value), suffix: firstConfig.suffix };
};

export const generateShortenNumberComponents = (
    num: number | undefined,
    decimalPlaces?: number,
    roundToInteger?: boolean
): { value: number | undefined; str: string | undefined; suffix: string } => {
    // Handle zero or empty input
    const zeroResult = handleZeroOrEmpty(num);
    if (zeroResult) return zeroResult;

    // Process number with optional rounding
    const roundingResult = processNumberWithRounding(num, roundToInteger);
    if (roundingResult.isZero) {
        return { value: 0, str: '0', suffix: '' };
    }

    let processedNum = roundingResult.value;
    const computedDecimalPlaces = getDecimalPlaces(decimalPlaces, processedNum, roundToInteger);

    processedNum = +processedNum.toFixed(computedDecimalPlaces);
    const factor = 10 ** computedDecimalPlaces;

    // Handle very small numbers
    if (processedNum === 0) {
        return { value: undefined, str: formatSmallNumber(computedDecimalPlaces), suffix: '' };
    }

    // Handle numbers less than 1000
    if (processedNum < 1000) {
        return { value: processedNum, str: String(processedNum), suffix: '' };
    }

    // Handle large numbers with scaling
    return formatLargeNumber(processedNum, factor);
};

/**
 * Shortens the number to a formatted string.
 * @param num - The number to shorten
 * @param decimalPlaces - Number of decimal places to show (optional)
 * @param roundToInteger - Whether to round the number to the nearest integer before formatting (default: false)
 */
export function shortenNumber(
    num: number | undefined,
    decimalPlaces?: number,
    roundToInteger?: boolean
): string {
    const { str, suffix } = generateShortenNumberComponents(num, decimalPlaces, roundToInteger);
    return str + suffix;
}
